<template>
    <main class="home-page app-screen">
        <header class="home-header">
            <div class="user-menu-container">
                <button class="user-button" type="button" @click="toggleUserMenu">
                    <div class="user-avatar">
                        {{
                            user?.first_name?.[0] ||
                            user?.username?.[0] ||
                            'U'
                        }}
                    </div>

                    <div class="user-meta">
                        <h2 class="user-name">
                            {{
                                user?.first_name ||
                                user?.username ||
                                'Пользователь'
                            }}
                        </h2>
                    </div>
                </button>

                <div v-if="showUserMenu" class="user-menu">
                    <button class="menu-item" type="button" @click="logout">
                        <LogOut />
                        Выйти
                    </button>
                </div>
            </div>

            <div class="header-actions">
                <div v-if="isOffline" class="offline-notification">
                    <Offline />
                </div>

                <button class="refresh-button" type="button" :disabled="loadingRefresh"
                    @click="refreshCemeteryList(true)">
                    <Refresh :class="{ 'spin-animation': loadingRefresh }" />
                </button>

                <button class="theme-button" type="button" @click="toggleTheme">
                    <Theme />
                </button>
            </div>
        </header>

        <div class="cemetery-section">
            <div v-if="!loadingCemetery" class="cemetery-search">
                <input v-model="searchQuery" class="cemetery-search-input" type="text" placeholder="Поиск кладбища" />

                <button v-if="searchQuery" class="clear-search-button" type="button" @click="searchQuery = ''">
                    ✕
                </button>
            </div>
            <div v-if="loadingCemetery" class="cemetery-loading">
                <div class="loader"></div>
            </div>

            <div v-else-if="!filteredCemeteries.length" class="cemetery-empty">
                {{ searchQuery ? 'Кладбища не найдены' : 'У вас нет доступных кладбищ' }}
            </div>

            <div v-else class="cemetery-list">
                <button v-for="item in filteredCemeteries" :key="item.cemetery_id" class="cemetery-card" type="button"
                    @click="goToCemetery(item)">
                    <div class="cemetery-card-left">
                        <div class="cemetery-info">
                            <strong>
                                {{ item.cemetery_name }}
                            </strong>

                            <span>
                                {{ item.municipality_name }}
                            </span>
                        </div>
                    </div>

                    <div class="cemetery-card-right">
                        <CardArrow />
                    </div>

                    <div class="cemetery-address">
                        {{ item.cemetery_address }}
                    </div>
                </button>
            </div>
        </div>
    </main>
</template>

<script setup lang="ts">
import {
    computed,
    onMounted,
    onUnmounted,
    ref,
    watch
} from 'vue'

import { useRouter } from 'vue-router'

import { useAuthStore } from '@/stores/authStore'
import { useCemeteryStore } from '@/stores/cemeteryStore'

import {
    getTheme,
    setTheme,
    type AppTheme
} from '@/app/theme'

import { LOADS_CEMETERY } from '@/scripts/api'
import { db, DB_VERSION } from '@/indexedDB/db'
import { create_UUID } from '@/utils/uuid'

import LogOut from '@/components/icons/LogOut.vue'
import Offline from '@/components/icons/Offline.vue'
import Refresh from '@/components/icons/Refresh.vue'
import Theme from '@/components/icons/Theme.vue'
import CardArrow from '@/components/icons/CardArrow.vue'

const router = useRouter()

const authStore = useAuthStore()
const cemeteryStore = useCemeteryStore()

const user = computed(() => authStore.user)

const cemetery = ref<any[]>([])
const loadingCemetery = ref(true)
const loadingRefresh = ref(false)
const syncInProgress = ref(false)
const lastSyncError = ref<Error | null>(null)

const theme = ref<AppTheme>(getTheme())
const showUserMenu = ref(false)
const isOffline = ref(false)
const searchQuery = ref('')

const visibleCemeteries = computed(() => {
    return cemetery.value.filter((item) => !item.access_revoked)
})

const filteredCemeteries = computed(() => {
    const query = searchQuery.value
        .trim()
        .toLowerCase()

    if (!query) {
        return visibleCemeteries.value
    }

    return visibleCemeteries.value.filter((item) => {
        return [
            item.cemetery_name,
            item.municipality_name,
            item.cemetery_address
        ]
            .filter(Boolean)
            .some((value) => {
                return String(value)
                    .toLowerCase()
                    .includes(query)
            })
    })
})

function toggleTheme() {
    const nextTheme = theme.value === 'light'
        ? 'dark'
        : 'light'

    setTheme(nextTheme)
    theme.value = nextTheme
}

function updateOnlineStatus() {
    isOffline.value = !navigator.onLine

    if (!isOffline.value && lastSyncError.value) {
        checkForUpdates()
    }
}

function toggleUserMenu() {
    showUserMenu.value = !showUserMenu.value
}

function handleClickOutside(event: MouseEvent) {
    const target = event.target as HTMLElement

    if (!target.closest('.user-menu-container')) {
        showUserMenu.value = false
    }
}

function logout() {
    authStore.logoutUser()

    router.push({
        name: 'login'
    })
}

async function initializeApp() {
    try {
        if (!authStore.user) {
            router.push({
                name: 'login'
            })

            return
        }

        await db.init(authStore.user.id)

        if (!db.isInitialized()) {
            throw new Error('Не удалось инициализировать IndexedDB')
        }

        await loadCachedData()

        if (!isOffline.value) {
            await checkForUpdates()
        }
    } catch (error) {
        console.error('Ошибка инициализации:', error)

        isOffline.value = true
    } finally {
        loadingCemetery.value = false
    }
}

async function loadCachedData() {
    const cachedCemetery = await db.getData<any>('cemetery')

    if (cachedCemetery?.length) {
        cemetery.value = cachedCemetery.map((item) => ({
            ...item,
            guid: item.guid || create_UUID()
        }))
    }
}

async function shouldSyncData() {
    try {
        const metadata = await db.getMetadata()
        const cachedCemetery = await db.getData<any>('cemetery')

        if (!cachedCemetery?.length) {
            return true
        }

        if (metadata.dbVersion !== DB_VERSION) {
            return true
        }

        if (metadata.userId !== (user.value?.id || null)) {
            return true
        }

        return false
    } catch (error) {
        console.error('Ошибка проверки синхронизации:', error)

        return true
    }
}

async function checkForUpdates() {
    try {
        const shouldSync = await shouldSyncData()

        if (shouldSync) {
            await syncAllData()
        }
    } catch (error) {
        console.error('Ошибка проверки обновлений:', error)
    }
}

async function syncAllData() {
    if (syncInProgress.value) return
    if (!db.isInitialized()) return

    try {
        syncInProgress.value = true
        lastSyncError.value = null
        isOffline.value = false

        await refreshCemeteryList(false)

        await db.updateMetadata({
            lastSync: new Date(),
            userId: user.value?.id || null,
            dbVersion: DB_VERSION
        })
    } catch (error) {
        console.error('Ошибка синхронизации:', error)

        lastSyncError.value = error as Error
        isOffline.value = true
    } finally {
        syncInProgress.value = false
    }
}

async function refreshCemeteryList(showLoader = true) {
    try {
        if (showLoader) {
            loadingRefresh.value = true
        }

        const response = await LOADS_CEMETERY({
            cemetery_id: null
        })

        const freshCemeteryList = response.data[0].info_json || []
        const currentCemeteryList = await db.getData<any>('cemetery') || []

        const freshCemeteryMap = new Map(
            freshCemeteryList.map((item: any) => [
                item.cemetery_id,
                item
            ])
        )

        const updatedCemeteryList = [
            ...currentCemeteryList.map((oldItem: any) => {
                const freshItem = freshCemeteryMap.get(oldItem.cemetery_id)

                if (freshItem) {
                    return {
                        ...freshItem,
                        guid: oldItem.guid || create_UUID(),
                        access_revoked: false
                    }
                }

                return {
                    ...oldItem,
                    access_revoked: true
                }
            }),

            ...freshCemeteryList
                .filter((freshItem: any) => {
                    return !currentCemeteryList.some((oldItem: any) => {
                        return oldItem.cemetery_id === freshItem.cemetery_id
                    })
                })
                .map((freshItem: any) => ({
                    ...freshItem,
                    guid: create_UUID(),
                    access_revoked: false
                }))
        ]

        await db.saveData('cemetery', updatedCemeteryList)

        cemetery.value = updatedCemeteryList

        await db.updateMetadata({
            lastSync: new Date(),
            userId: user.value?.id || null,
            dbVersion: DB_VERSION
        })
    } catch (error: any) {
        console.error('Ошибка обновления кладбищ:', error)

        if (error?.message === 'Network Error') {
            isOffline.value = true
            return
        }

        throw error
    } finally {
        loadingRefresh.value = false
    }
}

function goToCemetery(item: any) {
    cemeteryStore.set_cemetery(item)

    router.push({
        name: 'cemetery',
        params: {
            cemetery_id: item.cemetery_id
        }
    })
}

watch(isOffline, (value) => {
    if (!value && lastSyncError.value) {
        checkForUpdates()
    }
})

onMounted(async () => {
    document.addEventListener(
        'click',
        handleClickOutside
    )

    window.addEventListener(
        'online',
        updateOnlineStatus
    )

    window.addEventListener(
        'offline',
        updateOnlineStatus
    )

    updateOnlineStatus()

    await initializeApp()
})

onUnmounted(() => {
    document.removeEventListener(
        'click',
        handleClickOutside
    )

    window.removeEventListener(
        'online',
        updateOnlineStatus
    )

    window.removeEventListener(
        'offline',
        updateOnlineStatus
    )
})
</script>

<style scoped>
.home-page {
    width: 100%;
    height: 100%;
    min-height: 0;

    display: flex;
    flex-direction: column;
    gap: 6px;

    padding: 5px;
    overflow: hidden;

    background:
        radial-gradient(circle at top left,
            rgba(255, 95, 3, 0.1),
            transparent 30vw),
        linear-gradient(180deg,
            var(--surface) 0%,
            var(--surface-2) 100%);
}

.home-header {
    width: 100%;
    padding: 10px 12px;
    position: relative;
    z-index: 100;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;

    background: var(--surface-card);

    border: 1px solid var(--border);
    border-radius: 14px;

    box-shadow:
        0 8px 24px rgba(7, 44, 44, 0.08);

    backdrop-filter: blur(14px);

    flex-shrink: 0;
}

.user-menu-container {
    position: relative;
    min-width: 0;
}

.user-button {
    max-width: 210px;
    padding: 4px;

    display: flex;
    align-items: center;
    gap: 8px;

    border: 0;
    border-radius: 10px;

    background: transparent;

    cursor: pointer;
}

.user-button:active {
    background:
        rgba(7, 44, 44, 0.06);
}

.user-avatar {
    width: 34px;
    height: 34px;
    min-width: 34px;

    display: flex;
    align-items: center;
    justify-content: center;

    color: #fff;

    background:
        linear-gradient(135deg,
            #072C2C,
            #0f5656);

    border-radius: 10px;

    font-size: 13px;
    font-weight: 700;
}

.user-meta {
    min-width: 0;
    text-align: left;
}

.user-name {
    max-width: 140px;

    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;

    color: var(--primary);

    font-size: 14px;
    font-weight: 600;
    line-height: 1.2;
}

.user-menu {
    position: absolute;

    top: calc(100% + 14px);
    left: -10px;

    width: 132px;
    padding: 4px;

    background: var(--surface-card);

    border: 1px solid var(--border);
    border-radius: 12px;

    box-shadow:
        0 10px 26px rgba(7, 44, 44, 0.14);

    backdrop-filter: blur(14px);

    z-index: 9999;
}

.menu-item {
    width: 100%;
    min-height: 34px;
    padding: 0 10px;

    display: flex;
    align-items: center;
    gap: 8px;

    border: 0;
    border-radius: 9px;

    color: var(--primary);
    background: transparent;

    font-size: 13px;
    font-weight: 600;

    cursor: pointer;
}

.header-actions {
    margin-left: auto;

    display: flex;
    align-items: center;
    gap: 12px;
}

.theme-button,
.refresh-button,
.offline-notification {
    width: 34px;
    height: 34px;
    min-width: 34px;

    display: flex;
    align-items: center;
    justify-content: center;

    border-radius: 10px;
}

.theme-button,
.refresh-button {
    border: 1px solid var(--border);

    color: #fff;
    background: var(--primary);

    cursor: pointer;
}

.refresh-button:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}

.spin-animation {
    animation: spin 1s linear infinite;
}

@keyframes spin {
    from {
        transform: rotate(0deg);
    }

    to {
        transform: rotate(360deg);
    }
}

.offline-notification {
    background:
        rgba(255, 193, 7, 0.14);

    border: 1px solid rgba(255, 193, 7, 0.22);

    color: #d97706;

    animation:
        offlinePulse 2s infinite;
}

@keyframes offlinePulse {
    0% {
        opacity: 1;
    }

    50% {
        opacity: .55;
    }

    100% {
        opacity: 1;
    }
}

.cemetery-section {
    display: flex;
    flex-direction: column;
    position: relative;
    z-index: 1;
    overflow: visible;
    flex: 1;

    min-height: 0;

    padding: 5px;

    background: var(--surface-card);

    border: 1px solid var(--border);
    border-radius: 18px;

    box-shadow:
        0 8px 24px rgba(7, 44, 44, 0.06);

    backdrop-filter: blur(14px);

    overflow: hidden;
}

.cemetery-loading {
    flex: 1;

    display: flex;
    align-items: center;
    justify-content: center;
}

.loader {
    width: 42px;
    height: 42px;

    border:
        3px solid rgba(7, 44, 44, 0.12);

    border-top-color: var(--primary);

    border-radius: 50%;

    animation:
        loaderSpin .8s linear infinite;
}

@keyframes loaderSpin {
    to {
        transform: rotate(360deg);
    }
}

.cemetery-empty {
    padding: 14px;

    color: var(--text-muted);

    font-size: 14px;
    font-weight: 600;
    text-align: center;

    background: var(--surface-card);

    border: 1px solid var(--border);
    border-radius: 14px;
}

.cemetery-list {
    display: flex;
    flex-direction: column;
    gap: 10px;

    flex: 1;

    min-height: 0;

    overflow-y: auto;
    -webkit-overflow-scrolling: touch;

    padding-right: 2px;
}

.cemetery-card {
    position: relative;

    width: 100%;
    min-height: 82px;

    padding: 14px;

    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;

    border:
        1px solid rgba(7, 44, 44, 0.08);

    border-radius: 14px;

    background:
        linear-gradient(180deg,
            rgba(255, 255, 255, .72),
            rgba(255, 255, 255, .58));

    text-align: left;

    cursor: pointer;

    transition:
        transform .18s ease,
        border-color .18s ease,
        background .18s ease;
}

:root[data-theme='dark'] .cemetery-card {
    background:
        linear-gradient(180deg,
            rgba(255, 255, 255, .04),
            rgba(255, 255, 255, .02));
}

.cemetery-card:hover {
    border-color:
        rgba(255, 255, 255, 0.24);
}

.cemetery-card-left {
    min-width: 0;

    display: flex;
    align-items: center;

    flex: 1;
}

.cemetery-info {
    min-width: 0;

    display: flex;
    flex-direction: column;
    gap: 3px;
}

.cemetery-info strong {
    overflow: hidden;

    text-overflow: ellipsis;
    white-space: nowrap;

    color: var(--primary);

    font-size: 15px;
    font-weight: 600;
}

.cemetery-info span {
    overflow: hidden;

    text-overflow: ellipsis;
    white-space: nowrap;

    color: var(--text-muted);

    font-size: 12px;
    font-weight: 500;
}

.cemetery-search {
    position: relative;
    margin-bottom: 10px;
    flex-shrink: 0;
}

.cemetery-search-input {
    width: 100%;
    height: 42px;

    padding: 0 42px 0 12px;

    color: var(--primary);
    background: var(--surface-card);

    border: 1px solid var(--border);
    border-radius: 12px;

    font-size: 14px;
    font-weight: 500;

    outline: none;
}

.cemetery-search-input::placeholder {
    color: var(--text-muted);
}

.cemetery-search-input:focus {
    border-color: var(--primary);
}

.clear-search-button {
    position: absolute;
    top: 50%;
    right: 8px;
    width: 26px;
    height: 26px;
    display: flex;
    align-items: center;
    justify-content: center;
    transform: translateY(-50%);
    border: 0;
    border-radius: 8px;
    background: transparent;
    color: var(--text-muted);
    font-size: 14px;
    font-weight: 700;
    cursor: pointer;
}

.clear-search-button:hover {
    background: rgba(255, 255, 255, 0.08);
}

.cemetery-card-right {
    display: flex;
    align-items: center;
    justify-content: center;

    height: 100%;

    color: var(--text-muted);
}

.cemetery-address {
    position: absolute;

    left: 14px;
    right: 42px;
    bottom: 12px;

    overflow: hidden;

    text-overflow: ellipsis;
    white-space: nowrap;

    color: var(--text-muted);

    font-size: 12px;
    line-height: 1.3;
}

@media (min-width: 768px) {
    .home-page {
        padding: 10px;
    }

    .home-header {
        width: min(100%, 800px);
        margin: 0 auto;
    }

    .cemetery-section {
        width: min(100%, 800px);
        margin: 0 auto;
    }
}
</style>